import React, { useState } from 'react';
import './Common.css';
import { apiService } from '../api/api';

const LoginPage = ({ onNavigate, onLogin }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ FORGOT PASSWORD STATE
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // ✅ FORCED PASSWORD CHANGE STATE
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    email: '', userId: null, resetToken: '', newPassword: '', confirmPassword: ''
  });
  const [passwordChangeStep, setPasswordChangeStep] = useState(1);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map((c) =>
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (forgotPasswordError) setForgotPasswordError('');
  };

  const handlePasswordChangeInput = (e) => {
    const { name, value } = e.target;
    setPasswordChangeData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) newErrors.password = 'Password is required';
    return newErrors;
  };

  const validatePasswordChange = () => {
    const newErrors = {};
    if (!passwordChangeData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordChangeData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    if (!passwordChangeData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    return newErrors;
  };

  const handleForgotPassword = async () => {
    setForgotPasswordError('');
    const email = formData.email.trim();

    if (!email) {
      setForgotPasswordError('Please enter your email address above, then click Forgot Password.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setForgotPasswordError('Please enter a valid email address above, then click Forgot Password.');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      // console.log('📤 POST /Auth/forgot-password for:', email);
      await apiService.forgotPassword(email);
      // console.log('✅ Reset link sent to:', email);

      // ✅ Navigate to ForgotPasswordPage showing the "Check Your Email" screen
      // skipToStep: 'sent'  →  shows the 📧 confirmation, NOT the set-password form
      // initialEmail is passed so the screen can display "we sent an email to <email>"
      onNavigate('forgotPassword', { email, skipToStep: 'sent' });

    } catch (err) {
      console.error('❌ Forgot password failed:', err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to send reset email. Please try again.';
      setForgotPasswordError(message);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // ✅ FORCED PASSWORD CHANGE (first-login flow)
  const resetPasswordWithToken = async () => {
    const validationErrors = validatePasswordChange();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }

    setLoading(true);
    setErrors({});
    try {
      await apiService.resetPassword({
        email: passwordChangeData.email,
        token: passwordChangeData.resetToken,
        newPassword: passwordChangeData.newPassword
      });
      // console.log('✅ Password reset successful');
      alert('Password changed successfully! Please log in with your new password.');
      setForcePasswordChange(false);
      setPasswordChangeStep(1);
      setPasswordChangeData({ email: '', userId: null, resetToken: '', newPassword: '', confirmPassword: '' });
      setFormData({ email: passwordChangeData.email, password: '' });
    } catch (error) {
      console.error('❌ Failed to reset password:', error);
      setErrors({ submit: error.response?.data?.message || 'Failed to reset password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setLoading(true);
    try {
      const response = await apiService.loginUser(formData);
      // console.log('✅ Login successful:', response);

      // ✅ FORCED PASSWORD CHANGE
      if (response.forcePasswordChange === true) {
        // console.log('🔒 Force password change required');
        setPasswordChangeData({
          email: formData.email, userId: response.userId,
          resetToken: '', newPassword: '', confirmPassword: ''
        });
        setForcePasswordChange(true);

        try {
          const tokenResponse = await apiService.forgotPasswordToken(formData.email);
          const token = tokenResponse.token || tokenResponse.data?.token || tokenResponse;
          if (!token) throw new Error('Failed to receive reset token');
          setPasswordChangeData(prev => ({ ...prev, resetToken: token }));
          setPasswordChangeStep(2);
        } catch (tokenError) {
          console.error('❌ Failed to request reset token:', tokenError);
          setErrors({ submit: 'Failed to initialize password change. Please try logging in again.' });
          setForcePasswordChange(false);
        }
        setLoading(false);
        return;
      }

      // ✅ EXTRACT USER ID
      let userId = null;
      if (response.userId) {
        userId = response.userId;
      } else if (response.user) {
        userId = response.user.id || response.user.userId || response.user.ID;
      } else if (response.userData) {
        userId = response.userData.id || response.userData.userId;
      } else if (response.accessToken) {
        try { const d = decodeJWT(response.accessToken); userId = d?.nameid || d?.sub || d?.userId || d?.id; } catch { }
      } else if (response.token) {
        try { const d = decodeJWT(response.token); userId = d?.nameid || d?.sub || d?.userId || d?.id; } catch { }
      }

      if (userId) localStorage.setItem('userId', String(userId));

      if (response.accessToken) localStorage.setItem('authToken', response.accessToken);
      else if (response.token) localStorage.setItem('authToken', response.token);
      if (response.refreshToken) localStorage.setItem('refreshToken', response.refreshToken);

      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userData', JSON.stringify(response));

      // ✅ ROLE-BASED REDIRECT
      const roleId = response.roleId;
      if (roleId === 1 || roleId === '1') {
        localStorage.setItem('userRole', 'admin');
        localStorage.setItem('roleId', '1');
        alert('Welcome Admin!');
        onNavigate('admin');
      } else {
        localStorage.setItem('userRole', 'user');
        localStorage.setItem('roleId', String(roleId || 2));
        alert('Login successful!');
        onLogin();
      }

    } catch (error) {
      console.error('❌ Login failed:', error);
      if (error.response) {
        setErrors({ submit: error.response.data?.message || error.response.data?.error || 'Invalid email or password' });
      } else if (error.request) {
        setErrors({ submit: 'Network error. Please check your connection.' });
      } else {
        setErrors({ submit: 'Login failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ FORCED PASSWORD CHANGE SCREEN
  if (forcePasswordChange) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h2>Password Change Required</h2>
              <p>For your security, please set a new password before continuing</p>
            </div>
            <div className="password-change-form">
              {errors.submit && (
                <div style={{ marginBottom: '15px', textAlign: 'center', color: '#e74c3c' }}>
                  {errors.submit}
                </div>
              )}
              {passwordChangeStep === 2 ? (
                <div className="step-container">
                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="form-group-content">
                      <div className="password-input-wrapper">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          id="newPassword" name="newPassword"
                          value={passwordChangeData.newPassword}
                          onChange={handlePasswordChangeInput}
                          className={`form-control ${errors.newPassword ? 'error' : ''}`}
                          placeholder="Enter new password (min 6 characters)"
                        />
                        <button type="button" className="toggle-password" onClick={() => setShowNewPassword(!showNewPassword)}>
                          {showNewPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                      {errors.newPassword && <span className="error-message">{errors.newPassword}</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <div className="form-group-content">
                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword" name="confirmPassword"
                          value={passwordChangeData.confirmPassword}
                          onChange={handlePasswordChangeInput}
                          className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                          placeholder="Confirm new password"
                        />
                        <button type="button" className="toggle-password" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                          {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                      {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                    </div>
                  </div>
                  <button onClick={resetPasswordWithToken} className="login-button" disabled={loading} style={{ width: '100%' }}>
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <p>⏳ Preparing password change form...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ NORMAL LOGIN FORM
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>Welcome</h1>
            <p>Please login to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {errors.submit && (
              <div style={{ marginBottom: '15px', textAlign: 'center', color: '#e74c3c' }}>
                {errors.submit}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="form-group-content">
                <input
                  type="email" id="email" name="email"
                  value={formData.email} onChange={handleChange}
                  className={`form-control ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="form-group-content">
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password" name="password"
                    value={formData.password} onChange={handleChange}
                    className={`form-control ${errors.password ? 'error' : ''}`}
                    placeholder="Enter your password"
                  />
                  <button type="button" className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
            </div>

            <div className="form-options">
              <div className="remember-me">
                <input type="checkbox" id="remember" />
                <label htmlFor="remember">Remember me</label>
              </div>
              <button
                type="button"
                className="forgot-password-link"
                onClick={handleForgotPassword}
                disabled={forgotPasswordLoading}
              >
                {forgotPasswordLoading ? 'Sending...' : 'Forgot Password?'}
              </button>
            </div>

            {forgotPasswordError && (
              <div style={{
                marginTop: '6px', marginBottom: '10px', padding: '10px 12px',
                backgroundColor: '#fff3f3', border: '1px solid #f5c6cb',
                borderRadius: '6px', color: '#e74c3c', fontSize: '13px',
              }}>
                ⚠️ {forgotPasswordError}
              </div>
            )}

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="login-footer">
            <p>
              Don't have an account?{' '}
              <button type="button" className="register-link" onClick={() => onNavigate('register')}>
                Register here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;